/**
 * Фонтан монет при смерти зомби (ISSUE-84): одна монета engine (`CoinDropState`) на экране — несколько. Сначала они
 * разлетаются дугой вокруг зомби, потом по очереди летят в счётчик монет HUD. Долетают к концу `flightTicks`, когда
 * engine прибавляет их в кошелёк, поэтому счётчик «подпрыгивает» вместе с прилётом.
 */
import type { Point } from "./iso";

/** Доля полёта на разлёт; дальше — к счётчику. */
const BURST = 0.4;
/** Последняя монета стартует к счётчику на столько позже первой (доля полёта). */
const STAGGER = 0.12;

/** Сколько монет на экране за одну монету engine: 4 за мелочь, до 8 за босса. */
export function fountainCount(value: number): number {
  return Math.min(8, 3 + Math.ceil(Math.log2(1 + Math.max(1, value))));
}

/** Детерминированное «случайное» 0…1 по id монеты и номеру: кадры одинаковы при перерисовке. */
function hash01(id: number, i: number, salt: number): number {
  let h = Math.imul(id ^ (i * 0x9e3779b1) ^ (salt * 0x85ebca6b), 0x27d4eb2d) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d) >>> 0;
  return ((h ^ (h >>> 13)) >>> 0) / 4294967296;
}

/**
 * Где монета `i` из `n` в момент `k` ∈ [0, 1] полёта. `from` — зомби, `to` — счётчик, `cell` — пикселей на клетку.
 * Пишет в `out` и возвращает масштаб спрайта (монета чуть уменьшается у счётчика).
 */
export function fountainPoint(
  k: number,
  id: number,
  i: number,
  n: number,
  from: Point,
  to: Point,
  cell: number,
  out: Point,
): number {
  // разлёт веером по кругу, по земле сплющен вдвое (изометрия)
  const angle = ((i + hash01(id, i, 1) * 0.6) / n) * Math.PI * 2;
  const r = cell * (0.35 + 0.35 * hash01(id, i, 2));
  const lx = from.x + Math.cos(angle) * r;
  const ly = from.y + Math.sin(angle) * r * 0.5;
  const peak = cell * (0.8 + 0.5 * hash01(id, i, 3));
  if (k < BURST) {
    const t = k / BURST;
    out.x = from.x + (lx - from.x) * t;
    out.y = from.y + (ly - from.y) * t - 4 * peak * t * (1 - t);
    return 1;
  }
  const start = BURST + (n > 1 ? (STAGGER * i) / (n - 1) : 0);
  if (k < start) {
    out.x = lx;
    out.y = ly;
    return 1;
  }
  // к счётчику с ускорением и лёгким изгибом вверх
  const m = Math.min(1, (k - start) / (1 - start)) ** 2;
  out.x = lx + (to.x - lx) * m;
  out.y = ly + (to.y - ly) * m - Math.sin(Math.PI * m) * cell * 0.6;
  return 1 - 0.3 * m;
}
